import { RootLayout } from '@/layouts/root';
import { createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import { ExternalRedirect } from '@/components/external-redirect';

import { SignInPage } from './sign-in';
import { SignUpPage } from './sign-up';
import { SettingsPage } from './settings/settings';
import { DashboardPage } from './dashboards/dashboard.tsx';
import { TrackingPage } from './trackings/tracking.tsx';
import { OnboardingPage } from './onboarding/onboarding';
import { GettingStartedPage } from './getting-started';
import { VerifyDomain } from './onboarding/verify-domain';
import { Welcome } from './onboarding/welcome';
import { DNSRecords } from './onboarding/dns-records';
import { OnboardingConfigurePixel } from '../routes/onboarding/onboarding-configure-pixel.tsx';
import { OnboardingConsent } from './onboarding/onboarding-consent.tsx';
import { RequestAccessPage } from './request-access.tsx';
import { HardRefresh } from '@/components/hard-refresh';
import { AppLayout } from '@/layouts/app-layout';
import { RequireAuthLayout } from '@/layouts/require-auth-layout';
import { HomePage } from './home.tsx';
import { RequireMerchantLayout } from '@/layouts/require-merchant-layout.tsx';
import { ErrorPage } from './error-page';
import { CompleteSetup } from './onboarding/complete-setup.tsx';
import { CampaignsRoutes } from './campaigns/routes';
import { SegmentsRoutes } from './segments/routes';
import { SMSEditorPlayground } from './sms-editor-playground.tsx';
import { VercelFeatureFlagPlayGround } from './vercel-feature-flag-playground.tsx';
import { RevenueMetricsDashboardPage } from './dashboards/revenue-metrics-dashboard.tsx';
import { ProfilesPages } from './profiles/profiles.tsx';

export const routes = createRoutesFromElements(
  <Route element={<RootLayout />}>
    <Route path="/sign-in/*" element={<SignInPage />} />
    <Route path="/sign-up/*" element={<SignUpPage />} />
    <Route path="/error" element={<ErrorPage />} />
    {/* special route to do frontend refresh, works after user signin with email + password */}
    <Route path="/auth/shopify" element={<HardRefresh />} />
    <Route path="/auth/shopify/install" element={<HardRefresh />} />
    <Route element={<RequireAuthLayout />}>
      <Route path="/" element={<HomePage />} />
      <Route path="/playground/sms-editor" element={<SMSEditorPlayground />} />
      <Route path="/playground/vercel-feature-flags" element={<VercelFeatureFlagPlayGround />} />
      <Route path="admin">
        <Route index element={<Navigate to="custom-app-creds" replace />} />
        <Route
          path="custom-app-creds"
          element={
            <ExternalRedirect to="https://getcontextual.retool.com/app/custom-app-credentials-admin" />
          }
        />
      </Route>
      {/* Ask the user to request access to a merchant or connect their own */}
      <Route path="/getting-started" element={<GettingStartedPage />} />
      <Route path="/merchants/:slug/request-access" element={<RequestAccessPage />} />
      <Route path="/merchants/:slug" element={<RequireMerchantLayout />}>
        {/* landing page AFTER user has auth through shopify */}
        <Route path="onboarding/*" element={<OnboardingPage />}>
          <Route index element={<Navigate to="setup/welcome" replace />} />
          <Route path="setup/welcome" element={<Welcome />} />
          <Route path="setup/verify-domain" element={<VerifyDomain />} />
          <Route path="setup/dns-records" element={<DNSRecords />} />
          <Route path="setup/consent" element={<OnboardingConsent />} />
          <Route path="setup/configure-pixel" element={<OnboardingConfigurePixel />} />
          <Route path="setup/complete-setup" element={<CompleteSetup />} />
        </Route>
        <Route path="*" element={<AppLayout />} errorElement={<ErrorPage />}>
          <Route path="dashboard" element={<RevenueMetricsDashboardPage />}>
            <Route index element={<Navigate to="default" replace />} />
            <Route path="default" element={<DashboardPage />} />
          </Route>
          <Route path="campaigns/*" element={<CampaignsRoutes />} />
          <Route path="segments/*" element={<SegmentsRoutes />} />
          <Route path="tracking/*" element={<TrackingPage />} />
          <Route path="profiles" element={<ProfilesPages />} />
          <Route path="settings/*" element={<SettingsPage />} />
        </Route>
      </Route>
    </Route>
    <Route
      path="*"
      element={
        <ErrorPage title="Page Not Found" message="The page you're looking for doesn't exist." />
      }
    />
  </Route>
);
